<template>
  <!-- Wyświetla zdjęcie pojazdu. Kliknięcie pozwala przełączać zdjęcia (jeśli dostępne) -->
  <img
    :src="srcImage || ''"
    class="img-fluid fitted-image"
    :class="{ 'vehicle-image--hidden': !srcImage }"
    @click="switchImage"
    @error="onImageElementError"
  >
</template>

<script>
import moment from 'moment'
import { buildImageKey, readImage, loadImage } from '@/helpers/vehicleImageCache'

export default {
  props: {
    vehicle: {required: true},                // Obiekt pojazdu
    imageService: {required: true},           // Serwis do pobierania zdjęć
    imageNumber: {required: false},           // Numer zdjęcia (opcjonalnie)
    noSwitch: {required: false},              // Czy można przełączać zdjęcia
    setImageWithNumber: {required: false},    // Zewnętrzne wymuszenie numeru zdjęcia
    source: {
      type: String,
      default: 'unknown'
    }
  },
  components: {},
  data() {
    return {
      srcImage: '',            // Źródło aktualnego zdjęcia (base64 lub url)
      moment: moment,          // Biblioteka do dat
      imageNumberUsed: '',     // Numer aktualnie wyświetlanego zdjęcia
      imageSwitchPossible: false,  // Czy możliwa jest zmiana zdjęcia kliknięciem
      // Znacznik ostatniego żądania — odpowiedź starszego (inny pojazd/inne zdjęcie) jest odrzucana.
      requestToken: 0,
    }
  },
  name: "VehicleImage", 
  created() {
    // Ustalenie, czy można przełączać zdjęcia
    if (this.noSwitch) {
      this.imageSwitchPossible = false;
    } else {
      this.imageSwitchPossible = this.noSwitch;
    }

    this.resetAndLoadForVehicle('created');

    // Obserwator na zewnętrzną zmianę numeru zdjęcia (props)
    this.$watch(
        () => this.setImageWithNumber,
        (newVal) => {
          const imagesCount = this.vehicle?.images?.length || 0;
          // Jeśli nowy numer jest dostępny w liście zdjęć, ustaw i pobierz to zdjęcie
          if (newVal && imagesCount >= newVal) {
            this.imageNumberUsed = newVal;
            this.getImage(this.vehicle, newVal, this.vehicle.location.id, this.getCreatedAt(this.vehicle));
            // Wywołaj callback do rodzica
            this.wasSet(newVal);
          }
        },
        {immediate: true}
    )

    this.$watch(
      () => this.vehicle,
      () => {
        this.resetAndLoadForVehicle('vehicle-changed');
      },
      { deep: false }
    );
  },
  methods: {
    resetAndLoadForVehicle() {
      const imagesCount = this.vehicle?.images?.length || 0;
      this.imageNumberUsed = this.imageNumber ? this.imageNumber : (imagesCount > 0 ? 1 : 0);
      this.srcImage = '';
      // getImage sprawdza cache synchronicznie, więc przy trafieniu zdjęcie wraca w tym samym ticku
      // i wyczyszczenie powyżej nie powoduje mignięcia.

      this.getImage(this.vehicle, this.getImageType(), this.vehicle.location.id, this.getCreatedAt(this.vehicle));
    },
    // Informuje rodzica, że ustawiono nowe zdjęcie
    wasSet(newVal){
      this.$emit("wasSet", newVal);
    },
    // Zwraca typ zdjęcia na podstawie numeru
    getImageType() {
      const images = this.vehicle?.images || [];
      if (this.imageNumberUsed !== 0 && images.length >= this.imageNumberUsed) {
        return this.vehicle.images[this.imageNumberUsed - 1].type;
      }
      return 0;
    },
    // Kliknięcie przełącza na kolejne zdjęcie (jeśli jest ich więcej niż jedno)
    switchImage() {
      if (!this.imageSwitchPossible) {
        let totalNumber = this.vehicle.images && this.vehicle.images.length > 0 ? this.vehicle.images.length : 0;
        if (totalNumber === this.imageNumberUsed) {
          this.imageNumberUsed = 1;
        } else {
          this.imageNumberUsed = this.imageNumberUsed + 1;
        }
        this.getImage(this.vehicle, this.getImageType(), this.vehicle.location.id, this.getCreatedAt(this.vehicle));
      }
    },
    // Pobiera zdjęcie pojazdu. Najpierw sprawdza wspólny cache (helpers/vehicleImageCache) —
    // zdjęcie pobrane na liście pomiarów nie jest dociągane po raz drugi w szczegółach.
    async getImage(vehicle, typeId, location, createdAt) {
      const token = ++this.requestToken;

      if (!typeId || typeId === 0) {
        this.srcImage = '';
        return undefined;
      }

      const shouldUseScale = this.source === 'vehicle-list' && Number(typeId) === 1;
      const keyBase = {
        scope: this.imageService?.imageCacheScope,
        vehicleId: vehicle.id,
        locationId: location,
        typeId,
      };
      const wantedKey = buildImageKey({ ...keyBase, scaled: shouldUseScale });

      // Dokładne trafienie w cache — nic nie pobieramy.
      const cached = readImage(wantedKey);
      if (cached) {
        this.srcImage = cached;
        return undefined;
      }

      // Szczegóły pomiaru: miniatura pobrana wcześniej przez listę służy jako natychmiastowy
      // podkład, dopóki nie dociągnie się wersja w pełnej rozdzielczości.
      let placeholderShown = false;
      if (!shouldUseScale) {
        const thumbnail = readImage(buildImageKey({ ...keyBase, scaled: true }));
        if (thumbnail) {
          this.srcImage = thumbnail;
          placeholderShown = true;
        }
      }

      try {
        const data = await loadImage(wantedKey, async () => {
          const fetched = shouldUseScale
            ? await this.imageService.getImageWithScale(vehicle.id, typeId, location)
            : await this.imageService.getImage(vehicle.id, typeId, location, createdAt);

          return this.blobToBase64(fetched);
        });

        // W międzyczasie mógł zmienić się pojazd lub numer zdjęcia — starsza odpowiedź nie nadpisuje nowszej.
        if (token !== this.requestToken) {
          return undefined;
        }

        this.srcImage = data || '';
      } catch (error) {
        if (token !== this.requestToken) {
          return undefined;
        }
        // Na potrzeby testów NIE podkładamy placeholdera. Jeśli pokazujemy już miniaturę z listy,
        // zostawiamy ją — jest lepsza niż puste miejsce.
        if (!placeholderShown) {
          this.srcImage = '';
        }
      }

      return undefined;
    },

    // Zamienia blob (np. z fetch) na base64 dla <img src="">
    blobToBase64(blob) {
      if (!(blob instanceof Blob)) {
        return Promise.resolve('');
      }
      const reader = new FileReader();
      return new Promise((resolve, reject) => {
        reader.onerror = () => reject(new Error('FileReader failed'));
        reader.onloadend = () => {
          resolve(reader.result);
        };
        reader.readAsDataURL(blob);
      });
    },
    onImageElementError() {
      // Na testy nie podmieniamy na placeholder.
      this.srcImage = '';
    },

    // Zwraca datę utworzenia rekordu pojazdu w formacie ISO (potrzebne do pobierania zdjęcia)
    getCreatedAt(vehicle) {
      return this.moment(vehicle.createdAt).toISOString(false);
    }
  }
}
</script>

<style scoped>
.vehicle-image--hidden {
  visibility: hidden;
}
</style>
